import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Grid,
  Input,
  InputGroup,
  InputRightAddon,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import { FilterChipItem } from "@/shared/components/filter/FilterChipList";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickMarginFilterUnit } from "@/features/pathpick/types";
import {
  getInvalidPathPickRangeFields,
  getPathPickMarginFilterUnit,
  PATH_PICK_MARGIN_RANGE_KEYS,
  PATH_PICK_RANGE_FIELDS,
  PathPickRangeFieldDefinition,
  PathPickRangeFilter,
} from "@/features/pathpick/utils/range-filter";

type RangeValueKind = "percent" | "won" | "count";

const getValueKind = (
  definition: PathPickRangeFieldDefinition,
  marginUnit: PathPickMarginFilterUnit
): RangeValueKind => {
  if (definition.kind === "sales") return "count";
  return marginUnit === "rate" ? "percent" : "won";
};

const getTitleKey = (
  definition: PathPickRangeFieldDefinition,
  marginUnit: PathPickMarginFilterUnit
) => {
  const market = definition.field.startsWith("kream") ? "kream" : "poizon";
  if (definition.kind === "sales") {
    return `path_pick:filter.range_${market}_sales`;
  }
  return `path_pick:filter.range_${market}_margin_${marginUnit}`;
};

// 값 종류별 입력 규칙 — 마진은 손해 구간도 거를 수 있게 음수를 받고, 소수는 마진율만 받는다.
const INPUT_PATTERNS: Record<RangeValueKind, RegExp> = {
  percent: /^-?\d*\.?\d*$/,
  won: /^-?\d*$/,
  count: /^\d*$/,
};

// 마진율은 소수점 둘째 자리까지, 금액·건수는 천 단위 콤마로 보여준다.
const formatRangeValue = (value: number, kind: RangeValueKind) =>
  value.toLocaleString("en-US", {
    maximumFractionDigits: kind === "percent" ? 2 : 0,
    useGrouping: kind !== "percent",
  });

// 입력 중간 상태("-", "1.")는 아직 숫자가 아니라 미입력으로 본다.
const parseRangeInput = (text: string): number | undefined => {
  if (!/\d/.test(text)) return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
};

interface RangeInputProps {
  value?: number;
  kind: RangeValueKind;
  unit: string;
  placeholder: string;
  isInvalid: boolean;
  onChange: (value?: number) => void;
}

// 범위 한쪽 입력 — 포커스 중에는 입력한 그대로, 포커스가 빠지면 콤마를 넣어 보여준다.
// (타이핑 중에 콤마를 끼우면 커서 위치를 따로 보정해야 해서 표시 시점을 나눴다)
function RangeInput({
  value,
  kind,
  unit,
  placeholder,
  isInvalid,
  onChange,
}: RangeInputProps) {
  const [text, setText] = useState<string>(
    value === undefined ? "" : formatRangeValue(value, kind)
  );
  const [isFocused, setIsFocused] = useState(false);

  // 외부 값이 바뀌면(초기화·칩 삭제·취소) 표시를 다시 맞춘다. 입력 중인 같은 값은 건드리지 않는다.
  useEffect(() => {
    if (parseRangeInput(text.replace(/,/g, "")) === value) return;
    if (value === undefined) {
      setText("");
      return;
    }
    setText(isFocused ? String(value) : formatRangeValue(value, kind));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/,/g, "");
    if (!INPUT_PATTERNS[kind].test(raw)) return;
    setText(raw);
    onChange(parseRangeInput(raw));
  };

  const handleFocus = () => {
    setIsFocused(true);
    setText((prev) => prev.replace(/,/g, ""));
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseRangeInput(text);
    setText(parsed === undefined ? "" : formatRangeValue(parsed, kind));
  };

  return (
    // 칸이 좁아져도 최소~최대가 한 줄에 들어가도록 입력·단위 여백을 줄이고 minWidth를 풀어 둔다.
    <InputGroup size={"sm"} minWidth={0}>
      <Input
        type={"text"}
        inputMode={kind === "percent" ? "decimal" : "numeric"}
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        isInvalid={isInvalid}
        minWidth={0}
        paddingX={"8px"}
        fontSize={"12px"}
        height={"32px"}
        borderColor={"#D4D4D1"}
        borderRadius={"4px 0 0 4px"}
        background={"#FFFFFF"}
        errorBorderColor={"#EE3F3F"}
        _placeholder={{ color: "#8F8F8C" }}
      />
      {/* 높이를 고정하지 않고 InputGroup(flex)의 stretch로 입력칸 높이를 따라간다 — 모바일 기기에서는
          전역 CSS(globals.css의 body.is-mobile-device input min-height: 40px)가 입력칸만 40px로 키워
          고정 32px이던 단위칸과 어긋났다. */}
      <InputRightAddon
        height={"auto"}
        paddingX={"6px"}
        borderColor={"#D4D4D1"}
        background={"#F7F9FA"}
        fontSize={"12px"}
        color={"#444440"}
      >
        {unit}
      </InputRightAddon>
    </InputGroup>
  );
}

interface Props {
  // 표시할 값 — 상세 필터에서는 드래프트다.
  value: PathPickRangeFilter;
  onChange: (patch: PathPickRangeFilter) => void;
}

// 상세 필터의 범위 영역 — 마진 필터 기준(마진율/마진액) + 크림·포이즌 마진/거래수 범위 4칸.
// 최소·최대 중 한쪽만 입력해도 적용되고, 둘 다 입력했는데 최소가 크면 그 칸에 오류를 띄운다.
function PathPickRangeFilterSection({ value, onChange }: Props) {
  const { t } = useI18nHelper();
  const marginUnit = getPathPickMarginFilterUnit(value);
  const invalidFields = getInvalidPathPickRangeFields(value);

  // 기준을 바꾸면 단위가 달라 기존 마진 값이 의미를 잃으므로 함께 비운다.
  const handleChangeMarginUnit = (next: string) => {
    const nextUnit = next as PathPickMarginFilterUnit;
    if (nextUnit === marginUnit) return;
    onChange({
      ...PATH_PICK_MARGIN_RANGE_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: undefined }),
        {} as PathPickRangeFilter
      ),
      // 디폴트(마진율)는 쿼리스트링에서 생략한다.
      marginUnit: nextUnit === "rate" ? undefined : nextUnit,
    });
  };

  return (
    <Flex flexDirection={"column"} gap={"8px"} minWidth={0}>
      <Flex alignItems={"center"} gap={"12px"} flexWrap={"wrap"}>
        <Text textStyle={"body-md-bold"} color={"#444440"}>
          {t("path_pick:filter.margin_unit")}
        </Text>
        <RadioGroup value={marginUnit} onChange={handleChangeMarginUnit}>
          <Flex gap={"16px"}>
            {(["rate", "amount"] as PathPickMarginFilterUnit[]).map((unit) => (
              <Radio
                key={unit}
                value={unit}
                size={"sm"}
                borderColor={"#D4D4D1"}
                _checked={{
                  background: "#1272EF",
                  borderColor: "#1272EF",
                  color: "#FFFFFF",
                }}
                _focusVisible={{ boxShadow: "none" }}
              >
                <Text fontSize={"12px"}>
                  {t(`path_pick:filter.margin_unit_${unit}`)}
                </Text>
              </Radio>
            ))}
          </Flex>
        </RadioGroup>
      </Flex>
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        gap={"8px"}
      >
        {PATH_PICK_RANGE_FIELDS.map((definition) => {
          const kind = getValueKind(definition, marginUnit);
          const unit = t(`path_pick:filter.range_unit_${kind}`);
          const isInvalid = invalidFields.includes(definition.field);
          return (
            <Flex
              key={definition.field}
              flexDirection={"column"}
              gap={"8px"}
              padding={"10px"}
              minWidth={0}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              background={"#FFFFFF"}
            >
              <Text textStyle={"body-md-bold"} color={"#444440"}>
                {t(getTitleKey(definition, marginUnit))}
              </Text>
              <Flex alignItems={"center"} gap={"4px"}>
                <RangeInput
                  value={value[definition.minKey] as number | undefined}
                  kind={kind}
                  unit={unit}
                  placeholder={t("path_pick:filter.range_min")}
                  isInvalid={isInvalid}
                  onChange={(next) => onChange({ [definition.minKey]: next })}
                />
                <Text color={"#8F8F8C"} flexShrink={0}>
                  ~
                </Text>
                <RangeInput
                  value={value[definition.maxKey] as number | undefined}
                  kind={kind}
                  unit={unit}
                  placeholder={t("path_pick:filter.range_max")}
                  isInvalid={isInvalid}
                  onChange={(next) => onChange({ [definition.maxKey]: next })}
                />
              </Flex>
              {isInvalid && (
                <Text textStyle={"caption-medium"} color={"#EE3F3F"}>
                  {t("path_pick:filter.range_invalid")}
                </Text>
              )}
            </Flex>
          );
        })}
      </Grid>
    </Flex>
  );
}

// 한쪽만 입력된 범위는 "이상"/"이하"로 읽힌다.
const getChipLabel = (
  t: ReturnType<typeof useI18nHelper>["t"],
  title: string,
  min: number | undefined,
  max: number | undefined,
  format: (value: number) => string
): string => {
  if (min !== undefined && max !== undefined) {
    return t("path_pick:filter.range_chip_between", {
      title,
      min: format(min),
      max: format(max),
    });
  }
  if (min !== undefined) {
    return t("path_pick:filter.range_chip_min", { title, min: format(min) });
  }
  return t("path_pick:filter.range_chip_max", {
    title,
    max: format(max as number),
  });
};

// 적용된 범위 값 → 필터 칩. 칩 하나가 한 항목(최소·최대)을 나타내고, 지우면 그 항목을 통째로 비운다.
export const usePathPickRangeFilterChips = (
  applied: PathPickRangeFilter,
  onRemove: (patch: PathPickRangeFilter) => void
): FilterChipItem[] => {
  const { t } = useI18nHelper();

  return useMemo(() => {
    const marginUnit = getPathPickMarginFilterUnit(applied);
    return PATH_PICK_RANGE_FIELDS.flatMap((definition) => {
      const min = applied[definition.minKey] as number | undefined;
      const max = applied[definition.maxKey] as number | undefined;
      if (min === undefined && max === undefined) return [];

      const kind = getValueKind(definition, marginUnit);
      const format = (value: number) =>
        t(`path_pick:filter.range_value_${kind}`, {
          value: formatRangeValue(value, kind),
        });
      const title = t(getTitleKey(definition, marginUnit));
      const label = getChipLabel(t, title, min, max, format);
      return [
        {
          id: `range-${definition.field}`,
          label,
          onRemove: () =>
            onRemove({
              [definition.minKey]: undefined,
              [definition.maxKey]: undefined,
            }),
        },
      ];
    });
  }, [applied, onRemove, t]);
};

export default PathPickRangeFilterSection;
